import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { useModels } from '@/api-models/models';

import { addImages, fileToDataUrl, isSupportedImageFile, supportsImages, validateImages } from '../images';

/**
 * Staged image attachments for a composer: file picker, clipboard paste and drag-and-drop, all
 * gated on whether `model` can read images.
 *
 * Shared by the thread composer and the new-session composer so both surfaces accept the same
 * files, enforce the same caps, and give the same rejection feedback.
 */
export function useImageAttachments(model: string | null | undefined) {
  const [images, setImages] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The catalogue is fetched once and never revalidated, so this is a cache read after the
  // first render rather than a request per composer.
  const { models } = useModels();
  const canAttach = supportsImages(models, model);

  // Switching to a text-only model must not leave a staged attachment that the next send would
  // silently drop (or the backend reject).
  useEffect(() => {
    if (canAttach || images.length === 0) return;
    setImages([]);
    toast(`Attachments removed — ${model ?? 'this model'} can't read images.`);
  }, [canAttach, model, images.length]);

  async function attach(files: File[]) {
    if (files.length === 0) return;
    if (!canAttach) {
      toast.error(
        `Image upload isn't available for ${model ?? 'this model'}. Switch to a vision model to attach images.`,
      );
      return;
    }

    const supported = files.filter(isSupportedImageFile);
    if (supported.length < files.length) {
      toast.error('Only PNG, JPEG, GIF, BMP, TIFF and PPM images can be attached.');
    }
    if (supported.length === 0) return;

    const encoded = await Promise.all(supported.map((f) => fileToDataUrl(f).catch(() => null)));
    const usable = encoded.filter((d): d is string => d !== null);
    if (usable.length < encoded.length) toast.error("Couldn't read some of those images.");

    setImages((staged) => {
      const { images: next, rejected } = addImages(staged, usable);
      for (const reason of rejected) toast.error(reason);
      return next;
    });
  }

  return {
    images,
    canAttach,
    /** True while a file drag is over the composer, for the drop-target highlight. */
    dragging,
    fileInputRef,
    openPicker: () => fileInputRef.current?.click(),
    remove: (index: number) => setImages((staged) => staged.filter((_, i) => i !== index)),
    clear: () => setImages([]),
    /** `null` when the staged set is sendable; otherwise the reason to show the user. */
    validate: () => validateImages(images),
    onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      void attach(Array.from(e.target.files ?? []));
      e.target.value = '';
    },
    onPaste: (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData.files);
      if (files.length === 0) return;
      e.preventDefault();
      void attach(files);
    },
    dropZoneProps: {
      onDragOver: (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      },
      onDragLeave: () => setDragging(false),
      onDrop: (e: React.DragEvent) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setDragging(false);
        void attach(Array.from(e.dataTransfer.files));
      },
    },
  };
}
